// ./src/components/features/Tags/TagColorPicker/TagColorPicker.tsx
//
// Tag color picker styled as a form input:
// - Rectangle color preview matching the height of other Mantine inputs
// - "Shuffle" action icon to cycle through random palette colors
// - "Pick" action icon to open Mantine's ColorPicker popover
// - Consistent visual language with TextInput, Select, etc.

import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Group,
  Text,
  InputWrapper,
  Popover,
  ActionIcon,
  Tooltip,
  ColorPicker,
  Stack,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  TAG_COLOR_PALETTE,
} from "@/contexts/ExpenseTagsContext/tagColors";
import { getMostContrastingColor } from "@/utils/colorHelpers";
import { AppIcon } from "@/components/generic";

// ============================================
// Types
// ============================================

export interface TagColorPickerProps {
  /** Field name for form binding */
  name?: string;

  /** Current selected color (hex) */
  value: string | null | undefined;

  /** Callback when color changes: (name, color) */
  onChange: (name: string, color: string) => void;

  /** Field label */
  label?: string;

  /** Description text */
  description?: string;

  /** Error message */
  error?: string;

  /** Whether the picker is disabled */
  disabled?: boolean;

  /** Size variant — matches Mantine input sizes */
  size?: "sm" | "md" | "lg";

  /** Whether the field is required */
  required?: boolean;
}

// ============================================
// Constants
// ============================================

/** Input heights matching Mantine's input size tokens */
const INPUT_HEIGHT = {
  sm: 36,
  md: 42,
  lg: 50,
};

const ICON_SIZE = {
  sm: 28,
  md: 34,
  lg: 40,
};

// ============================================
// Helper: Pick a random palette color different from current
// ============================================

const getRandomPaletteColor = (currentColor?: string | null): string => {
  const candidates = currentColor
    ? TAG_COLOR_PALETTE.filter(
      (c) => c.toUpperCase() !== (currentColor || "").toUpperCase()
    )
    : TAG_COLOR_PALETTE;

  const pool = candidates.length > 0 ? candidates : TAG_COLOR_PALETTE;
  return pool[Math.floor(Math.random() * pool.length)];
};

// ============================================
// Component
// ============================================

const TagColorPickerComponent: React.FC<TagColorPickerProps> = ({
  name = "tagColor",
  value,
  onChange,
  label,
  description,
  error,
  disabled = false,
  size = "md",
  required = false,
}) => {
  const { t } = useTranslation(["tags"]);

  const [pickerOpened, setPickerOpened] = useState(false);

  const height = INPUT_HEIGHT[size];
  const iconSize = ICON_SIZE[size];

  // The displayed color (fallback to a default gray if nothing set)
  const displayColor = value || "#6B7280";

  // Contrasting color for text on the swatch
  const contrastColor = useMemo(
    () => getMostContrastingColor(displayColor),
    [displayColor]
  );

  // Handle shuffle — pick a random palette color
  const handleShuffle = useCallback(() => {
    if (disabled) return;
    const newColor = getRandomPaletteColor(value);
    onChange(name, newColor);
  }, [disabled, value, onChange, name]);

  // Handle color picker change
  const handlePickerChange = useCallback(
    (color: string) => {
      if (disabled) return;
      onChange(name, color.toUpperCase());
    },
    [disabled, onChange, name]
  );

  return (
    <InputWrapper
      label={label}
      description={description}
      error={error}
      required={required}
      size={size}
    >
      <Group gap="xs" align="stretch" wrap="nowrap" mt={4}>
        {/* Color preview — styled as an input field */}
        <Box
          style={{
            flex: 1,
            height,
            borderRadius: "var(--mantine-radius-default)",
            backgroundColor: displayColor,
            border: error
              ? "1px solid var(--mantine-color-red-6)"
              : "1px solid var(--mantine-color-gray-4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color 200ms ease",
            opacity: disabled ? 0.6 : 1,
            minWidth: 0,
          }}
        >
          <Text size="xs" ff="monospace" c={contrastColor} fw={500}>
            {displayColor}
          </Text>
        </Box>

        {/* Shuffle button */}
        <Tooltip
          label={t("tags:form.fields.tagColor.shuffle", {
            defaultValue: "Random color",
          })}
          withArrow
          position="top"
        >
          <ActionIcon
            variant="default"
            size={height}
            onClick={handleShuffle}
            disabled={disabled}
            aria-label={t("tags:form.fields.tagColor.shuffle", {
              defaultValue: "Random color",
            })}
            style={{ flexShrink: 0 }}
          >
            <AppIcon iconName="Refresh" size={iconSize * 0.5} />
          </ActionIcon>
        </Tooltip>

        {/* Color picker popover */}
        <Popover
          opened={pickerOpened}
          onChange={setPickerOpened}
          position="bottom-end"
          withArrow
          shadow="md"
          trapFocus
        >
          <Popover.Target>
            <Tooltip
              label={t("tags:form.fields.tagColor.pick", {
                defaultValue: "Pick color",
              })}
              withArrow
              position="top"
              disabled={pickerOpened}
            >
              <ActionIcon
                variant="default"
                size={height}
                onClick={() => setPickerOpened((o) => !o)}
                disabled={disabled}
                aria-label={t("tags:form.fields.tagColor.pick", {
                  defaultValue: "Pick color",
                })}
                style={{ flexShrink: 0 }}
              >
                <AppIcon iconName="Palette" size={iconSize * 0.5} />
              </ActionIcon>
            </Tooltip>
          </Popover.Target>

          <Popover.Dropdown>
            <Stack gap="sm" style={{ width: 240 }}>
              <Text size="sm" fw={500}>
                {t("tags:form.fields.tagColor.pickLabel", {
                  defaultValue: "Choose a color",
                })}
              </Text>
              <ColorPicker
                value={displayColor}
                onChange={handlePickerChange}
                format="hex"
                swatches={TAG_COLOR_PALETTE as any}
                swatchesPerRow={6}
                size="sm"
                fullWidth
              />
            </Stack>
          </Popover.Dropdown>
        </Popover>
      </Group>
    </InputWrapper>
  );
};

export const TagColorPicker = React.memo(TagColorPickerComponent);